import { emptyApi as api } from './emptyApi';
export const addTagTypes = [
  'auth',
  'permissions',
  'trip',
  'trips',
  'stories',
  'uploads',
  'profile-uploads',
  'profile',
  'publisher',
  'publish',
  'interactions',
] as const;
const injectedRtkApi = api
  .enhanceEndpoints({
    addTagTypes,
  })
  .injectEndpoints({
    endpoints: (build) => ({
      postAuth: build.mutation<PostAuthApiResponse, PostAuthApiArg>({
        query: (queryArg) => ({
          url: `/auth/`,
          method: 'POST',
          body: queryArg.tokenBodyRequest,
          params: { remember_me: queryArg.rememberMe },
        }),
        invalidatesTags: ['auth'],
      }),
      deleteAuthAccount: build.mutation<DeleteAuthAccountApiResponse, DeleteAuthAccountApiArg>({
        query: () => ({ url: `/auth/account`, method: 'DELETE' }),
        invalidatesTags: ['auth'],
      }),
      postAuthRefresh: build.mutation<PostAuthRefreshApiResponse, PostAuthRefreshApiArg>({
        query: () => ({ url: `/auth/refresh`, method: 'POST' }),
        invalidatesTags: ['auth'],
      }),
      postAuthLogout: build.mutation<PostAuthLogoutApiResponse, PostAuthLogoutApiArg>({
        query: () => ({ url: `/auth/logout`, method: 'POST' }),
        invalidatesTags: ['auth'],
      }),
      getAuthPermissions: build.query<GetAuthPermissionsApiResponse, GetAuthPermissionsApiArg>({
        query: () => ({ url: `/auth/permissions` }),
        providesTags: ['permissions'],
      }),
      getAuthInspect: build.query<GetAuthInspectApiResponse, GetAuthInspectApiArg>({
        query: () => ({ url: `/auth/inspect` }),
        providesTags: ['auth'],
      }),
      getTripsByTripId: build.query<GetTripsByTripIdApiResponse, GetTripsByTripIdApiArg>({
        query: (queryArg) => ({ url: `/trips/${queryArg}` }),
        providesTags: ['trip'],
      }),
      patchTripsByTripId: build.mutation<PatchTripsByTripIdApiResponse, PatchTripsByTripIdApiArg>({
        query: (queryArg) => ({
          url: `/trips/${queryArg.tripId}`,
          method: 'PATCH',
          body: queryArg.partialTripBodyRequest,
        }),
        invalidatesTags: ['trip'],
      }),
      deleteTripsByTripId: build.mutation<
        DeleteTripsByTripIdApiResponse,
        DeleteTripsByTripIdApiArg
      >({
        query: (queryArg) => ({ url: `/trips/${queryArg}`, method: 'DELETE' }),
        invalidatesTags: ['trip', 'auth'],
      }),
      getTrips: build.query<GetTripsApiResponse, GetTripsApiArg>({
        query: (queryArg) => ({ url: `/trips/`, params: { role: queryArg } }),
        providesTags: ['trips'],
      }),
      postTrips: build.mutation<PostTripsApiResponse, PostTripsApiArg>({
        query: (queryArg) => ({ url: `/trips/`, method: 'POST', body: queryArg }),
        invalidatesTags: ['trips'],
      }),
      getTripsByUserByUserId: build.query<
        GetTripsByUserByUserIdApiResponse,
        GetTripsByUserByUserIdApiArg
      >({
        query: (queryArg) => ({ url: `/trips/byUser/${queryArg}` }),
        providesTags: ['trips'],
      }),
      getStoriesByTripByTripId: build.query<
        GetStoriesByTripByTripIdApiResponse,
        GetStoriesByTripByTripIdApiArg
      >({
        query: (queryArg) => ({ url: `/stories/byTrip/${queryArg}` }),
        providesTags: ['stories'],
      }),
      postStoriesByTripByTripId: build.mutation<
        PostStoriesByTripByTripIdApiResponse,
        PostStoriesByTripByTripIdApiArg
      >({
        query: (queryArg) => ({
          url: `/stories/byTrip/${queryArg.tripId}`,
          method: 'POST',
          body: queryArg.storyBodyRequest,
        }),
        invalidatesTags: ['stories'],
      }),
      postStoriesByTripByTripIdStub: build.mutation<
        PostStoriesByTripByTripIdStubApiResponse,
        PostStoriesByTripByTripIdStubApiArg
      >({
        query: (queryArg) => ({ url: `/stories/byTrip/${queryArg}/stub`, method: 'POST' }),
        invalidatesTags: ['stories'],
      }),
      patchStoriesByTripByTripIdStoryAndStoryId: build.mutation<
        PatchStoriesByTripByTripIdStoryAndStoryIdApiResponse,
        PatchStoriesByTripByTripIdStoryAndStoryIdApiArg
      >({
        query: (queryArg) => ({
          url: `/stories/byTrip/${queryArg.tripId}/story/${queryArg.storyId}`,
          method: 'PATCH',
          body: queryArg.partialStoryBodyRequest,
        }),
        invalidatesTags: ['stories'],
      }),
      deleteStoriesByTripByTripIdStoryAndStoryId: build.mutation<
        DeleteStoriesByTripByTripIdStoryAndStoryIdApiResponse,
        DeleteStoriesByTripByTripIdStoryAndStoryIdApiArg
      >({
        query: (queryArg) => ({
          url: `/stories/byTrip/${queryArg.tripId}/story/${queryArg.storyId}`,
          method: 'DELETE',
        }),
        invalidatesTags: ['stories'],
      }),
      postUploadsByTripByTripIdGenerateUrls: build.mutation<
        PostUploadsByTripByTripIdGenerateUrlsApiResponse,
        PostUploadsByTripByTripIdGenerateUrlsApiArg
      >({
        query: (queryArg) => ({
          url: `/uploads/byTrip/${queryArg.tripId}/generate-urls`,
          method: 'POST',
          body: queryArg.mediaUploadRequest,
        }),
        invalidatesTags: ['uploads'],
      }),
      postUploadsProfilePicture: build.mutation<
        PostUploadsProfilePictureApiResponse,
        PostUploadsProfilePictureApiArg
      >({
        query: (queryArg) => ({ url: `/uploads/profilePicture`, method: 'POST', body: queryArg }),
        invalidatesTags: ['profile-uploads'],
      }),
      getProfiles: build.query<GetProfilesApiResponse, GetProfilesApiArg>({
        query: () => ({ url: `/profiles/` }),
        providesTags: ['profile'],
      }),
      getProfilesByUserId: build.query<GetProfilesByUserIdApiResponse, GetProfilesByUserIdApiArg>({
        query: (queryArg) => ({ url: `/profiles/${queryArg}` }),
        providesTags: ['profile'],
      }),
      putProfilesByUserId: build.mutation<
        PutProfilesByUserIdApiResponse,
        PutProfilesByUserIdApiArg
      >({
        query: (queryArg) => ({
          url: `/profiles/${queryArg.userId}`,
          method: 'PUT',
          body: queryArg.profileBodyRequest,
        }),
        invalidatesTags: ['profile'],
      }),
      getPublisherTripsByTripIdStatus: build.query<
        GetPublisherTripsByTripIdStatusApiResponse,
        GetPublisherTripsByTripIdStatusApiArg
      >({
        query: (queryArg) => ({ url: `/publisher/trips/${queryArg}/status` }),
        providesTags: ['publisher'],
      }),
      postPublisherTripsByTripIdPublish: build.mutation<
        PostPublisherTripsByTripIdPublishApiResponse,
        PostPublisherTripsByTripIdPublishApiArg
      >({
        query: (queryArg) => ({ url: `/publisher/trips/${queryArg}/publish`, method: 'POST' }),
        invalidatesTags: ['publish'],
      }),
      getInteractionsByTripByTripIdAndLinkSecret: build.query<
        GetInteractionsByTripByTripIdAndLinkSecretApiResponse,
        GetInteractionsByTripByTripIdAndLinkSecretApiArg
      >({
        query: (queryArg) => ({
          url: `/interactions/byTrip/${queryArg.tripId}/${queryArg.linkSecret}`,
        }),
        providesTags: ['interactions'],
      }),
      postInteractionsByTripByTripIdAndLinkSecretComment: build.mutation<
        PostInteractionsByTripByTripIdAndLinkSecretCommentApiResponse,
        PostInteractionsByTripByTripIdAndLinkSecretCommentApiArg
      >({
        query: (queryArg) => ({
          url: `/interactions/byTrip/${queryArg.tripId}/${queryArg.linkSecret}/comment`,
          method: 'POST',
          body: queryArg.commentBodyRequest,
        }),
        invalidatesTags: ['interactions'],
      }),
      postInteractionsByTripByTripIdAndLinkSecretLike: build.mutation<
        PostInteractionsByTripByTripIdAndLinkSecretLikeApiResponse,
        PostInteractionsByTripByTripIdAndLinkSecretLikeApiArg
      >({
        query: (queryArg) => ({
          url: `/interactions/byTrip/${queryArg.tripId}/${queryArg.linkSecret}/like`,
          method: 'POST',
          body: queryArg.likeBodyRequest,
        }),
        invalidatesTags: ['interactions'],
      }),
      postInteractionsByTripByTripIdAndLinkSecretViews: build.mutation<
        PostInteractionsByTripByTripIdAndLinkSecretViewsApiResponse,
        PostInteractionsByTripByTripIdAndLinkSecretViewsApiArg
      >({
        query: (queryArg) => ({
          url: `/interactions/byTrip/${queryArg.tripId}/${queryArg.linkSecret}/views`,
          method: 'POST',
        }),
        invalidatesTags: ['interactions'],
      }),
      getInteractionsByTripByTripIdViews: build.query<
        GetInteractionsByTripByTripIdViewsApiResponse,
        GetInteractionsByTripByTripIdViewsApiArg
      >({
        query: (queryArg) => ({ url: `/interactions/byTrip/${queryArg.tripId}/views` }),
        providesTags: ['interactions'],
      }),
      deleteInteractionsByTripByTripIdCommentAndCommentId: build.mutation<
        DeleteInteractionsByTripByTripIdCommentAndCommentIdApiResponse,
        DeleteInteractionsByTripByTripIdCommentAndCommentIdApiArg
      >({
        query: (queryArg) => ({
          url: `/interactions/byTrip/${queryArg.tripId}/comment/${queryArg.commentId}`,
          method: 'DELETE',
        }),
        invalidatesTags: ['interactions'],
      }),
    }),
    overrideExisting: false,
  });
export { injectedRtkApi as backendApi };
export type PostAuthApiResponse = /** status 200 session cookie */ SessionResponse;
export type PostAuthApiArg = {
  /** Allows userrs to stay logged in longer */
  rememberMe?: boolean;
  tokenBodyRequest: TokenBodyRequest;
};
export type DeleteAuthAccountApiResponse = /** status 200 undefined */ void;
export type DeleteAuthAccountApiArg = void;
export type PostAuthRefreshApiResponse = /** status 200 new session cookies */ GenericResponse;
export type PostAuthRefreshApiArg = void;
export type PostAuthLogoutApiResponse = /** status 200 Logout response */ LogoutResponse;
export type PostAuthLogoutApiArg = void;
export type GetAuthPermissionsApiResponse = /** status 200 Permissions */ PermissionsResponse;
export type GetAuthPermissionsApiArg = void;
export type GetAuthInspectApiResponse = /** status 200 Session */ InspectResponse;
export type GetAuthInspectApiArg = void;
export type GetTripsByTripIdApiResponse = /** status 200 Trip */ TripResponse;
export type GetTripsByTripIdApiArg = /** tripId */ string;
export type PatchTripsByTripIdApiResponse = /** status 200 Trip */ TripResponse;
export type PatchTripsByTripIdApiArg = {
  /** tripId */
  tripId: string;
  partialTripBodyRequest: PartialTripBodyRequest;
};
export type DeleteTripsByTripIdApiResponse =
  /** status 200 Trip deleted successfully */ DeleteTripResponse;
export type DeleteTripsByTripIdApiArg = /** tripId */ string;
export type GetTripsApiResponse = /** status 200 Trips */ TripsResponse;
export type GetTripsApiArg = /** role to filter with */ 'admin' | 'viewer';
export type PostTripsApiResponse = /** status 200 Trips */ PostTripResponse;
export type PostTripsApiArg = TripBodyRequest;
export type GetTripsByUserByUserIdApiResponse = /** status 200 Trips */ TripsResponse;
export type GetTripsByUserByUserIdApiArg = /** userId */ string;
export type GetStoriesByTripByTripIdApiResponse = /** status 200 Stories */ StoriesResponse;
export type GetStoriesByTripByTripIdApiArg = /** tripId */ string;
export type PostStoriesByTripByTripIdApiResponse = /** status 200 Stories */ StoryResponse;
export type PostStoriesByTripByTripIdApiArg = {
  /** tripId */
  tripId: string;
  storyBodyRequest: StoryBodyRequest;
};
export type PostStoriesByTripByTripIdStubApiResponse = /** status 200 Stories */ StubStoryResponse;
export type PostStoriesByTripByTripIdStubApiArg = /** tripId */ string;
export type PatchStoriesByTripByTripIdStoryAndStoryIdApiResponse =
  /** status 200 Stories */ StoryResponse;
export type PatchStoriesByTripByTripIdStoryAndStoryIdApiArg = {
  /** tripId */
  tripId: string;
  /** storyId */
  storyId: string;
  partialStoryBodyRequest: PartialStoryBodyRequest;
};
export type DeleteStoriesByTripByTripIdStoryAndStoryIdApiResponse =
  /** status 200 Stories */ DeleteResponse;
export type DeleteStoriesByTripByTripIdStoryAndStoryIdApiArg = {
  /** tripId */
  tripId: string;
  /** storyId */
  storyId: string;
};
export type PostUploadsByTripByTripIdGenerateUrlsApiResponse =
  /** status 200 Trip */ MediaUploadResponse;
export type PostUploadsByTripByTripIdGenerateUrlsApiArg = {
  /** tripId */
  tripId: string;
  mediaUploadRequest: MediaUploadRequest;
};
export type PostUploadsProfilePictureApiResponse = /** status 200 Trip */ MediaUploadResponse;
export type PostUploadsProfilePictureApiArg = ProfilePicUploadRequest;
export type GetProfilesApiResponse = /** status 200 Profile */ OwnProfileResponse;
export type GetProfilesApiArg = void;
export type GetProfilesByUserIdApiResponse = /** status 200 Profile */ ProfileResponse;
export type GetProfilesByUserIdApiArg = /** userId */ string;
export type PutProfilesByUserIdApiResponse = /** status 200 Profile */ ProfileResponse;
export type PutProfilesByUserIdApiArg = {
  /** userId */
  userId: string;
  profileBodyRequest: ProfileBodyRequest;
};
export type GetPublisherTripsByTripIdStatusApiResponse =
  /** status 200 Publish status */ PublishStatusResponse;
export type GetPublisherTripsByTripIdStatusApiArg = /** tripId */ string;
export type PostPublisherTripsByTripIdPublishApiResponse = /** status 200 Trip */ PublishResponse;
export type PostPublisherTripsByTripIdPublishApiArg = /** tripId */ string;
export type GetInteractionsByTripByTripIdAndLinkSecretApiResponse =
  /** status 200 interactions */ InteractionsResponse;
export type GetInteractionsByTripByTripIdAndLinkSecretApiArg = {
  /** tripId */
  tripId: string;
  /** linkSecret */
  linkSecret: string;
};
export type PostInteractionsByTripByTripIdAndLinkSecretCommentApiResponse =
  /** status 200 interactions */ any;
export type PostInteractionsByTripByTripIdAndLinkSecretCommentApiArg = {
  /** tripId */
  tripId: string;
  /** linkSecret */
  linkSecret: string;
  commentBodyRequest: CommentBodyRequest;
};
export type PostInteractionsByTripByTripIdAndLinkSecretLikeApiResponse =
  /** status 200 success */ any;
export type PostInteractionsByTripByTripIdAndLinkSecretLikeApiArg = {
  /** tripId */
  tripId: string;
  /** linkSecret */
  linkSecret: string;
  likeBodyRequest: LikeBodyRequest;
};
export type PostInteractionsByTripByTripIdAndLinkSecretViewsApiResponse =
  /** status 201 success */ any;
export type PostInteractionsByTripByTripIdAndLinkSecretViewsApiArg = {
  /** tripId */
  tripId: string;
  /** linkSecret */
  linkSecret: string;
};
export type GetInteractionsByTripByTripIdViewsApiResponse =
  /** status 200 interactions */ ViewsResponse;
export type GetInteractionsByTripByTripIdViewsApiArg = {
  /** tripId */
  tripId: string;
  /** linkSecret */
  linkSecret: string;
};
export type DeleteInteractionsByTripByTripIdCommentAndCommentIdApiResponse =
  /** status 201 interactions */ any;
export type DeleteInteractionsByTripByTripIdCommentAndCommentIdApiArg = {
  /** tripId */
  tripId: string;
  /** commentId */
  commentId: string;
};
export type SessionResponse = {
  sessionId: string;
};
export type TokenBodyRequest = {
  idToken: string;
};
export type GenericResponse = {
  success: boolean;
};
export type LogoutResponse = {
  success: boolean;
};
export type PermissionsResponse = {
  globalPermissions: string[];
  permissions: {
    tripId: string;
    role: string;
    userId: string;
  }[];
};
export type InspectResponse = {
  /** The user id */
  userId: string;
  /** The timestamp of the session expiry in seconds */
  sessionExpiresAt: number;
  /** Whether the user has a remember me session */
  rememberMe: boolean;
  /** The timestamp of the remember me expiry in seconds */
  rememberMeExpiresAt?: number;
};
export type TripResponse = {
  id: string;
  title: string;
  startDate: string;
  endDate: string;
  postCount: number;
  ownerId: string;
  headerFileName: string;
  shareMode: 'PUBLIC' | 'LINK' | 'PRIVATE';
  description: string;
  slug: string;
  linkSecret?: string;
  customCss?: string;
};
export type PartialTripBodyRequest = {
  title?: string;
  description?: string;
  startDate?: string;
  endDate?: string;
  headerFileName?: string;
  shareMode?: 'PUBLIC' | 'LINK' | 'PRIVATE';
  slug?: string;
  customCss?: string;
};
export type DeleteTripResponse = {
  tripId: string;
};
export type TripsResponse = TripResponse[];
export type PostTripResponse = {
  tripId: string;
};
export type TripBodyRequest = {
  title: string;
  description: string;
  startDate: string;
  endDate: string;
  headerFileName: string;
  shareMode: 'PUBLIC' | 'LINK' | 'PRIVATE';
  slug?: string;
  customCss?: string;
};
export type StoriesResponse = {
  stories: {
    id: string;
    tripId: string;
    title: string;
    description?: string;
    date: string;
    place?: string;
    medias: {
      id: string;
      ext: string;
      dimensions: {
        width: number;
        height: number;
      };
    }[];
  }[];
};
export type StoryResponse = {
  id: string;
  tripId: string;
  title: string;
  description?: string;
  date: string;
  place?: string;
  medias: {
    id: string;
    ext: string;
    dimensions: {
      width: number;
      height: number;
    };
  }[];
};
export type StoryBodyRequest = {
  title: string;
  description?: string;
  date?: string;
  place?: string;
  medias: {
    id: string;
    ext: string;
    dimensions: {
      width: number;
      height: number;
    };
  }[];
};
export type StubStoryResponse = {
  id: string;
};
export type PartialStoryBodyRequest = {
  title?: string;
  description?: string;
  date?: string;
  place?: string;
  medias?: {
    id: string;
    ext: string;
    dimensions: {
      width: number;
      height: number;
    };
  }[];
};
export type DeleteResponse = {
  /** id of deleted story */
  id: string;
};
export type MediaUploadResponse = {
  uploadUrls: {
    filename: string;
    uploadUrl: string;
    uploadId: string;
  }[];
};
export type MediaUploadRequest = {
  storyId?: string;
  filenames: {
    filename: string;
  }[];
};
export type ProfilePicUploadRequest = {
  filename: string;
};
export type OwnProfileResponse = {
  userId: string;
  username: string;
  email: string;
  creationYear: string;
};
export type ProfileResponse = {
  userId: string;
  username: string;
  email?: string;
  creationYear: string;
};
export type ProfileBodyRequest = {
  username: string;
};
export type PublishStatusResponse = {
  tripId: string;
  hasUnpublishedChanges: boolean;
};
export type PublishResponse = {
  tripId: string;
  success: boolean;
};
export type InteractionsResponse = {
  stories: {
    storyId: string;
    likes: {
      userId: string;
    }[];
    comments: {
      id: string;
      name?: string;
      userId?: string;
      body: string;
      /** creation time in ISO format */
      createdAt: string;
    }[];
  }[];
};
export type CommentBodyRequest = {
  storyId: string;
  name?: string;
  userId?: string;
  body: string;
};
export type LikeBodyRequest = {
  storyId: string;
};
export type ViewsResponse = {
  viewCount?: number;
};
export const {
  usePostAuthMutation,
  useDeleteAuthAccountMutation,
  usePostAuthRefreshMutation,
  usePostAuthLogoutMutation,
  useGetAuthPermissionsQuery,
  useLazyGetAuthPermissionsQuery,
  useGetAuthInspectQuery,
  useLazyGetAuthInspectQuery,
  useGetTripsByTripIdQuery,
  useLazyGetTripsByTripIdQuery,
  usePatchTripsByTripIdMutation,
  useDeleteTripsByTripIdMutation,
  useGetTripsQuery,
  useLazyGetTripsQuery,
  usePostTripsMutation,
  useGetTripsByUserByUserIdQuery,
  useLazyGetTripsByUserByUserIdQuery,
  useGetStoriesByTripByTripIdQuery,
  useLazyGetStoriesByTripByTripIdQuery,
  usePostStoriesByTripByTripIdMutation,
  usePostStoriesByTripByTripIdStubMutation,
  usePatchStoriesByTripByTripIdStoryAndStoryIdMutation,
  useDeleteStoriesByTripByTripIdStoryAndStoryIdMutation,
  usePostUploadsByTripByTripIdGenerateUrlsMutation,
  usePostUploadsProfilePictureMutation,
  useGetProfilesQuery,
  useLazyGetProfilesQuery,
  useGetProfilesByUserIdQuery,
  useLazyGetProfilesByUserIdQuery,
  usePutProfilesByUserIdMutation,
  useGetPublisherTripsByTripIdStatusQuery,
  useLazyGetPublisherTripsByTripIdStatusQuery,
  usePostPublisherTripsByTripIdPublishMutation,
  useGetInteractionsByTripByTripIdAndLinkSecretQuery,
  useLazyGetInteractionsByTripByTripIdAndLinkSecretQuery,
  usePostInteractionsByTripByTripIdAndLinkSecretCommentMutation,
  usePostInteractionsByTripByTripIdAndLinkSecretLikeMutation,
  usePostInteractionsByTripByTripIdAndLinkSecretViewsMutation,
  useGetInteractionsByTripByTripIdViewsQuery,
  useLazyGetInteractionsByTripByTripIdViewsQuery,
  useDeleteInteractionsByTripByTripIdCommentAndCommentIdMutation,
} = injectedRtkApi;
